import React, { useState, useEffect, useCallback } from 'react';
import './contact.css';
import Footer from '../Component/NewFooter';
import SplashScreen from '../Component/SplashScreen';
import axios from 'axios';

const BASE_URL      = "https://api.jeyshawnng.com";
const FETCH_TIMEOUT = 8000;
// Bumped to v2: older cached payloads (a different shape than what
// parseContactData currently produces) were making it past the "cached
// exists" check and crashing the render with pageData shaped wrong.
const CACHE_KEY      = "jeyshawn_contact_data_v2";

/* ── Helpers ───────────────────────────────────────────────────── */
function parseContactData(db) {
  return {
    heroImage:    db?.heroImage    || "",
    sectionTitle: db?.sectionTitle || "",
    offices: Array.isArray(db?.offices)
      ? db.offices
          .slice()
          .sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0))
          .map((office, idx) => ({
            id:      office?._id || idx,
            name:    office?.name    || "",
            address: office?.address || null,
            emails:  Array.isArray(office?.emails) ? office.emails : [],
            phones:  Array.isArray(office?.phones) ? office.phones : [],
          }))
      : [],
  };
}

// Guards against a stale/malformed localStorage payload (e.g. cached
// under an older shape) being treated as valid and crashing the render
// before the fetch can replace it with fresh, correctly-shaped data.
function isValidPageData(obj) {
  return (
    obj &&
    typeof obj === "object" &&
    typeof obj.heroImage === "string" &&
    typeof obj.sectionTitle === "string" &&
    Array.isArray(obj.offices)
  );
}

function readCache() {
  try {
    const parsed = JSON.parse(localStorage.getItem(CACHE_KEY));
    if (!isValidPageData(parsed)) {
      localStorage.removeItem(CACHE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}
function writeCache(data) {
  try { localStorage.setItem(CACHE_KEY, JSON.stringify(data)); } catch {}
}

/* ── Sub-components ────────────────────────────────────────────── */
function ErrorScreen({ onRetry }) {
  return (
    <div style={{
      position: "fixed", inset: 0, background: "#09090b", zIndex: 9999,
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "20px",
    }}>
      <div style={{ textAlign: "center" }}>
        <p style={{ color: "rgba(255,255,255,0.85)", fontSize: "1.1rem", fontFamily: "Georgia, serif", letterSpacing: "0.04em", marginBottom: "8px" }}>
          Poor internet connection.
        </p>
        <p style={{ color: "rgba(255,255,255,0.35)", fontSize: "0.8rem", letterSpacing: "0.08em", textTransform: "uppercase" }}>
          Please check your connection and try again.
        </p>
      </div>
      <button
        onClick={onRetry}
        style={{
          marginTop: "8px", padding: "10px 32px", background: "transparent",
          border: "1px solid rgba(255,255,255,0.25)", color: "rgba(255,255,255,0.7)",
          fontSize: "0.8rem", letterSpacing: "0.12em", textTransform: "uppercase",
          cursor: "pointer", fontFamily: "Georgia, serif", transition: "border-color 0.2s, color 0.2s",
        }}
        onMouseEnter={e => { e.target.style.borderColor = "#d4af72"; e.target.style.color = "#d4af72"; }}
        onMouseLeave={e => { e.target.style.borderColor = "rgba(255,255,255,0.25)"; e.target.style.color = "rgba(255,255,255,0.7)"; }}
      >
        Try Again
      </button>
    </div>
  );
}

/* ── Page ──────────────────────────────────────────────────────── */
const Contact = () => {
  const cached = readCache();

  const [status, setStatus]     = useState(cached ? "ready" : "splash-loading");
  const [pageData, setPageData] = useState(cached || null);
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', company: '', message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const doFetch = useCallback(async (background = false) => {
    const controller = new AbortController();
    const timeoutId  = setTimeout(() => controller.abort(), FETCH_TIMEOUT);

    try {
      const { data } = await axios.get(`${BASE_URL}/api/contact`, { signal: controller.signal });
      clearTimeout(timeoutId);

      const parsed = parseContactData(data?.data);
      writeCache(parsed);
      setPageData(parsed);

      if (!background) setStatus("splash-done");
    } catch (err) {
      clearTimeout(timeoutId);
      if (background) {
        console.warn("Background refresh failed for /api/contact:", err.message);
      } else {
        setStatus("error");
      }
    }
  }, []);

  useEffect(() => {
    doFetch(!!cached);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError(null);
    try {
      await axios.post(`${BASE_URL}/api/contact/submit`, formData);
      alert('Message sent! We will get back to you shortly.');
      setFormData({ name: '', email: '', phone: '', company: '', message: '' });
    } catch (err) {
      console.error('Failed to submit contact form:', err);
      setSubmitError('Something went wrong sending your message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (status === "error") {
    return <ErrorScreen onRetry={() => { setStatus("splash-loading"); doFetch(false); }} />;
  }
  if (status === "splash-loading") return <SplashScreen blocking />;
  // Defense in depth: don't fall through to the main render with no
  // usable data, whatever the reason — show the splash instead of
  // crashing on data.heroImage.
  if (!pageData) return <SplashScreen blocking />;

  const data = pageData;
  const offices = data.offices || [];

  return (
    <>
      {status === "splash-done" && (
        <SplashScreen autoExit onDone={() => setStatus("ready")} />
      )}

      <div className="img_carrier">
        {data.heroImage && <img src={data.heroImage} alt="" />}
      </div>

      <section className="contact-section" aria-labelledby="contact-title">
        <div className="contact-section__header">
          <h2 className="contact-section__title" id="contact-title">{data.sectionTitle}</h2>
          <hr className="contact-section__divider" />
        </div>

        <hr className="contact-section__rule" />   

        <div className="contact-section__body">

          {/* Info Panel */}
          <div className="contact-info">
            {offices.map((office) => (
              <div className="contact-info__office" key={office.id}>
                <h3 className="contact-info__office-name">{office.name}</h3>

                {office.address && (
                  <p className="contact-info__address">{office.address}</p>
                )}

                {office.phones.length > 0 && (
                  <ul className="contact-info__phones">
                    {office.phones.map((phone) => (
                      <li key={phone}>Tel: {phone}</li>
                    ))}
                  </ul>
                )}

                {office.emails.length > 0 && (
                  <div className="contact-info__email">
                    <span className="contact-info__email-label">Email:</span>
                    {office.emails.map((email) => (
                      <p key={email} className="contact-info__email-value">{email}</p>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Form Panel */}
          <div className="contact-form-panel">
            <h3 className="contact-form__title">Send Message</h3>
            <form onSubmit={handleSubmit} noValidate>
              <div className="contact-form__row">
                <input className="contact-form__input" type="text" name="name" placeholder="Name"
                  value={formData.name} onChange={handleChange} required />
                <input className="contact-form__input" type="email" name="email" placeholder="Email"
                  value={formData.email} onChange={handleChange} required />
              </div>
              <div className="contact-form__row">
                <input className="contact-form__input" type="tel" name="phone" placeholder="Phone"
                  value={formData.phone} onChange={handleChange} />
                <input className="contact-form__input" type="text" name="company" placeholder="Company"
                  value={formData.company} onChange={handleChange} />
              </div>
              <div className="contact-form__field">
                <textarea className="contact-form__textarea" name="message" placeholder="Message"
                  value={formData.message} onChange={handleChange} required />
              </div>
              <button type="submit" className="contact-form__submit" disabled={submitting}>
                {submitting ? 'Sending...' : 'Send Message'}
              </button>
              {submitError && (
                <p style={{ color: '#dc2626', fontSize: 13, marginTop: 8 }}>{submitError}</p>
              )}
            </form>
          </div>

        </div>
      </section>
      <Footer />
    </>
  );
};

export default Contact;